import React, { useState } from "react";
import { apiService, CampaignType } from "../../services";

interface CampaignFormData {
  name: string;
  description: string;
  startDate: string;
  endDate: string;
  product: string;
  productQuantity: number;
  status: "active" | "inactive";
  productDescription: string;
  giftName?: string;
  webLink?: string;
  rateCode: number;
  winLimit?: number;
  type: CampaignType;
}

interface CampaignCreatePageProps {
  onPageChange: (page: string) => void;
}

const CampaignCreatePage: React.FC<CampaignCreatePageProps> = ({
  onPageChange,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState<CampaignFormData>({
    name: "",
    description: "",
    startDate: "",
    endDate: "",
    product: "",
    productQuantity: 0,
    status: "active",
    productDescription: "",
    giftName: "",
    webLink: "",
    rateCode: 1,
    winLimit: 0,
    type: "normal",
  });
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  // Không cần tính toán tổng số mã code nữa

  // Không cần helper functions cho batch code nữa

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value, type } = e.target;

    if (type === "number") {
      // Allow empty string for clearing, only convert to number if not empty
      const numVal = value === "" ? 0 : parseFloat(value);
      if (!isNaN(numVal)) {
        setFormData((prev) => ({ ...prev, [name]: numVal }));
      }
      if (errors[name as keyof typeof errors])
        setErrors((prev) => ({ ...prev, [name]: "" }));
      return;
    }

    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name as keyof typeof errors])
      setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleTypeChange = (type: CampaignType) => {
    setFormData((prev) => ({ ...prev, type }));
  };

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.name) {
      newErrors.name = "Tên chiến dịch là bắt buộc";
    }
    if (formData.type === "normal" && !formData.product) {
      newErrors.product = "Tên sản phẩm là bắt buộc";
    }
    if (!formData.startDate) {
      newErrors.startDate = "Ngày bắt đầu là bắt buộc";
    }
    if (!formData.endDate) {
      newErrors.endDate = "Ngày kết thúc là bắt buộc";
    }
    if (!formData.webLink) {
      newErrors.webLink = "Link trang web là bắt buộc";
    }
    if (formData.rateCode <= 0) {
      newErrors.rateCode = "Tỷ lệ code phải lớn hơn 0";
    }

    if (formData.startDate && formData.endDate) {
      const startDate = new Date(formData.startDate);
      const endDate = new Date(formData.endDate);
      if (startDate >= endDate) {
        newErrors.endDate = "Ngày kết thúc phải sau ngày bắt đầu";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate form
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    try {
      const isVoucher = formData.type === "voucher";
      const newCampaignData = {
        name: formData.name,
        description: formData.description,
        product_name: isVoucher
          ? formData.product || formData.name
          : formData.product,
        gift_name: isVoucher ? formData.giftName || "" : formData.giftName || "",
        total_gift: isVoucher ? 0 : formData.productQuantity,
        rate_code: formData.rateCode,
        rate_gift: 1,
        batch: `BATCH_${Date.now()}`,
        start_date: formData.startDate,
        end_date: formData.endDate,
        landing_page_link: formData.webLink || "",
        api_key: `api_key_${Date.now()}`,
        win_limit: formData.winLimit || 0,
        status: formData.status === "active" ? 1 : 2,
        type: formData.type,
      };

      const resp: any = await apiService.createCampaign(newCampaignData);

      if (resp && resp.success === false) {
        setErrors((prev) => ({
          ...prev,
          general:
            resp.message || "Không thể tạo chiến dịch. Vui lòng thử lại.",
        }));
        return;
      }

      onPageChange("campaigns");
    } catch (error: any) {
      console.error("Failed to create campaign:", error);
      // Hiển thị lỗi chung
      setErrors((prev) => ({
        ...prev,
        general:
          (error && error.message) ||
          "Không thể tạo chiến dịch. Vui lòng kiểm tra lại thông tin.",
      }));
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    onPageChange("campaigns");
  };

  const handleExportTemplate = () => {
    const templateContent = [
      [
        "Mã Batch",
        "Tên chiến dịch",
        "Sản phẩm",
        "Số lượng sản phẩm",
        "Ngày bắt đầu",
        "Ngày kết thúc",
        "Tỷ lệ đổi",
        "Tỷ lệ quà",
        "Tỷ lệ code",
        "Mô tả",
      ],
      [
        "BATCH-001",
        "Chiến dịch mẫu",
        "Sản phẩm A",
        "1000",
        "2024-01-01",
        "2024-12-31",
        "1",
        "1",
        "1",
        "Mô tả chiến dịch mẫu",
      ],
    ];

    const csvString = templateContent
      .map((row) => row.map((cell) => `"${cell}"`).join(","))
      .join("\n");

    const blob = new Blob([csvString], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", "campaign_template.csv");
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleExportCurrentForm = () => {
    const currentFormData = [
      [
        "Mã Batch",
        "Tên chiến dịch",
        "Sản phẩm",
        "Số lượng sản phẩm",
        "Ngày bắt đầu",
        "Ngày kết thúc",
        "Tỷ lệ đổi",
        "Tỷ lệ quà",
        "Tỷ lệ code",
        "Mô tả",
        "Trạng thái",
      ],
      [
        "NEW",
        formData.name || "",
        formData.product || "",
        formData.productQuantity.toString() || "0",
        formData.startDate || "",
        formData.endDate || "",
        formData.rateCode.toString() || "1", // Code rate
        formData.description || "",
        formData.status === "active" ? "Bật" : "Tắt",
      ],
    ];

    const csvString = currentFormData
      .map((row) => row.map((cell) => `"${cell}"`).join(","))
      .join("\n");

    const blob = new Blob([csvString], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", `campaign_new.csv`);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleExportAllExcel = async () => {
    try {
      // Fetch all campaigns
      const response = await apiService.getCampaigns();
      const allCampaigns = response.data;

      // Export all campaigns data to Excel
      const allCampaignsData = [
        [
          "Mã Batch",
          "Tên chiến dịch",
          "Sản phẩm",
          "Số lượng sản phẩm",
          "Ngày bắt đầu",
          "Ngày kết thúc",
          "Tỷ lệ đổi",
          "Trạng thái",
          "Mô tả",
          "Link web",
        ],
        ...allCampaigns.map((camp) => [
          camp.batch,
          camp.name,
          camp.product_name,
          camp.total_gift.toString(),
          camp.start_date,
          camp.end_date,
          `${camp.rate_gift} quà: ${camp.rate_code} code`,
          camp.status === 1 ? "Đang chạy" : "Đang dừng",
          camp.description || "",
          camp.landing_page_link || "",
        ]),
      ];

      const csvString = allCampaignsData
        .map((row) => row.map((cell) => `"${cell}"`).join(","))
        .join("\n");

      const blob = new Blob([csvString], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute("download", "all_campaigns.csv");
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error: any) {
      console.error("Failed to export campaigns:", error);
      alert("Không thể xuất file Excel");
    }
  };

  return (
    <div className="w-full">
      {/* Header */}
      <div className="mb-8 flex justify-between items-start">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Tạo Chiến Dịch Mới
          </h1>
          <p className="text-gray-600">
            Nhập thông tin chi tiết để tạo chiến dịch marketing mới
          </p>
        </div>
      </div>

      {/* Form */}
      <form
        onSubmit={handleSubmit}
        className="bg-white rounded-lg shadow-sm border border-gray-200 px-4 py-4 w-full"
      >
        <div className="grid grid-cols-1 lg:grid-cols-2 2xl:grid-cols-3 gap-6 lg:gap-8">
          {/* Cột 1: Thông tin cơ bản */}
          <div className="space-y-6">
            <h3 className="text-lg font-semibold text-gray-800 border-b border-gray-200 pb-2">
              Thông Tin Cơ Bản
            </h3>

            {/* Loại chiến dịch */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Loại chiến dịch <span className="text-red-500">*</span>
              </label>
              <div className="flex gap-4">
                <label className="inline-flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="type"
                    checked={formData.type === "normal"}
                    onChange={() => handleTypeChange("normal")}
                    className="h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-800">Thường</span>
                </label>
                <label className="inline-flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="type"
                    checked={formData.type === "voucher"}
                    onChange={() => handleTypeChange("voucher")}
                    className="h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-800">Voucher</span>
                </label>
              </div>
            </div>

            {/* Tên chiến dịch */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tên Chiến Dịch <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                  errors.name ? "border-red-500" : "border-gray-300"
                }`}
                placeholder="Nhập tên chiến dịch..."
                required
              />
              {errors.name && (
                <p className="mt-1 text-sm text-red-600">{errors.name}</p>
              )}
            </div>

            {/* Link dẫn web */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Link Dẫn Web <span className="text-red-500">*</span>
              </label>
              <input
                type="url"
                name="webLink"
                value={formData.webLink || ""}
                onChange={handleInputChange}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                  errors.webLink ? "border-red-500" : "border-gray-300"
                }`}
                placeholder="https://gift.pregnavie.vn/"
                required
              />
              {errors.webLink && (
                <p className="mt-1 text-sm text-red-600">{errors.webLink}</p>
              )}
            </div>

            {/* Mô tả */}
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Mô Tả
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                rows={6}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none placeholder-gray-400 placeholder-opacity-100 h-32"
                placeholder="Nhập mô tả chiến dịch..."
              />
            </div>
          </div>

          {/* Cột 2: Thông tin sản phẩm và thời gian */}
          <div className="space-y-6">
            <h3 className="text-lg font-semibold text-gray-800 border-b border-gray-200 pb-2">
              Sản Phẩm & Thời Gian
            </h3>

            {/* Tên sản phẩm */}
            {formData.type === "normal" && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Tên Sản Phẩm <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="product"
                  value={formData.product}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                    errors.product ? "border-red-500" : "border-gray-300"
                  }`}
                  placeholder="Nhập tên sản phẩm..."
                  required
                />
                {errors.product && (
                  <p className="mt-1 text-sm text-red-600">{errors.product}</p>
                )}
              </div>
            )}

            {/* Tên quà và số lượng quà — ẩn với chiến dịch voucher */}
            {formData.type === "normal" && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Tên Quà
                  </label>
                  <input
                    type="text"
                    name="giftName"
                    value={formData.giftName || ""}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Nhập tên quà tặng..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Số Lượng Quà
                  </label>
                  <input
                    type="number"
                    name="productQuantity"
                    value={formData.productQuantity}
                    onChange={handleInputChange}
                    onWheel={(e) => e.currentTarget.blur()}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                      errors.productQuantity
                        ? "border-red-500"
                        : "border-gray-300"
                    }`}
                    placeholder="0"
                  />
                </div>
              </div>
            )}

            {/* Tỷ lệ code */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tỷ Lệ Code <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                name="rateCode"
                value={formData.rateCode}
                onChange={handleInputChange}
                min="1"
                onWheel={(e) => e.currentTarget.blur()}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                  errors.rateCode ? "border-red-500" : "border-gray-300"
                }`}
                placeholder="1"
                required
              />
              {errors.rateCode && (
                <p className="mt-1 text-sm text-red-600">{errors.rateCode}</p>
              )}
            </div>

            {/* Giới hạn trúng thưởng */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Giới Hạn Trúng Thưởng <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                name="winLimit"
                value={formData.winLimit || 0}
                onChange={handleInputChange}
                min="1"
                onWheel={(e) => e.currentTarget.blur()}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                  errors.winLimit ? "border-red-500" : "border-gray-300"
                }`}
                placeholder="Nhập số lần trúng thưởng tối đa"
                required
              />
              <p className="mt-1 text-sm text-gray-500">
                Số lần tối đa một số điện thoại có thể trúng thưởng
              </p>
              {errors.winLimit && (
                <p className="mt-1 text-sm text-red-600">{errors.winLimit}</p>
              )}
            </div>

            {/* Thời gian bắt đầu và kết thúc ngang hàng */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Thời Gian Bắt Đầu <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  name="startDate"
                  value={formData.startDate}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                    errors.startDate ? "border-red-500" : "border-gray-300"
                  }`}
                  required
                />
                {errors.startDate && (
                  <p className="mt-1 text-sm text-red-600">
                    {errors.startDate}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Thời Gian Kết Thúc <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  name="endDate"
                  value={formData.endDate}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                    errors.endDate ? "border-red-500" : "border-gray-300"
                  }`}
                  required
                />
                {errors.endDate && (
                  <p className="mt-1 text-sm text-red-600">{errors.endDate}</p>
                )}
              </div>
            </div>
          </div>

          {/* Cột 3: Cấu hình và trạng thái */}
          <div className="space-y-6 lg:col-span-2 2xl:col-span-1">
            <h3 className="text-lg font-semibold text-gray-800 border-b border-gray-200 pb-2">
              Cấu Hình & Trạng Thái
            </h3>

            {/* Trạng thái chiến dịch - cuối cột */}
            <div className="pt-4">
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium text-gray-700">
                  Trạng thái
                </label>
                <div className="flex items-center space-x-3">
                  <span className="text-sm text-gray-700">
                    {formData.status === "active" ? "Đang chạy" : "Đang dừng"}
                  </span>
                  <button
                    type="button"
                    onClick={() =>
                      setFormData((prev) => ({
                        ...prev,
                        status:
                          prev.status === "active" ? "inactive" : "active",
                      }))
                    }
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                      formData.status === "active"
                        ? "bg-blue-500"
                        : "bg-gray-200"
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        formData.status === "active"
                          ? "translate-x-6"
                          : "translate-x-1"
                      }`}
                    />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {errors.general && (
          <div className="mt-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
            {errors.general}
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex justify-end space-x-4 mt-10 pt-8 border-t border-gray-200">
          <button
            type="button"
            onClick={handleCancel}
            className="px-4 py-3 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-transparent transition-colors"
          >
            Hủy
          </button>
          <button
            type="submit"
            disabled={isLoading}
            className="px-4 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? "Đang tạo..." : "Tạo Chiến Dịch"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CampaignCreatePage;
